import * as React from 'react';
import {ReactNode} from 'react';

import {captureException, withScope} from '@sentry/browser';

import {AppConfig} from '../types';
import {ErrorScreen} from './ErrorScreen';

type FallbackProps = {
  errorId?: string;
  sentryEventId?: string;
};

type Props = {
  fallback: React.ReactElement<FallbackProps>;
  children: React.ReactNode;
};

type State = {
  error: Error | null;
  eventId: string | null;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = {error: null, eventId: null};

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({error});
    withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key as keyof React.ErrorInfo]);
      });
      const eventId = captureException(error);
      this.setState({eventId});
    });
  }

  render() {
    const {error, eventId} = this.state;
    const {children, fallback} = this.props;

    if (error === null) {
      return children;
    }

    return React.cloneElement(fallback, {
      errorId: eventId ?? undefined,
      sentryEventId: eventId ?? undefined,
    });
  }
}

export function DefaultErrorBoundary({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}) {
  const Fallback = ({errorId}: {errorId?: string}) => {
    return <ErrorScreen config={config} errorId={errorId} />;
  };

  return <ErrorBoundary fallback={<Fallback />}>{children}</ErrorBoundary>;
}
